import { DefaultBrandId, DefaultModelId } from '@studio/models/device';
import type { Brand, Model } from '@studio/models/device';
import type { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isAuthInvalid, isTransitory } from './gating.js';
import type { Gateable } from './gating.js';

export type SetupAttention = 'authentication' | 'driver';

/** Registry template fields the driver verdicts read. */
type DriverModel = T_Maybe<{
  name?: T_Maybe<string>;
  generic?: T_Maybe<boolean>;
  brandId?: T_Maybe<Brand['id']>;
}>;

/** Generic registry models can have IDs other than the built-in fallback model. */
export const isGenericDriver = (modelId: T_Maybe<Model['id']>, model?: DriverModel): boolean =>
  !modelId ||
  modelId === DefaultModelId ||
  model?.generic === true ||
  (model?.generic === undefined && !!model?.name?.toLowerCase().includes('other'));

/**
 * The user chose "Other – brand is not listed": a model of the registry's catch-all brand other
 * than DefaultModelId, the hidden fallback every unidentified camera is saved with.
 */
export const isUnlistedBrandDriver = (
  modelId: T_Maybe<Model['id']>,
  model?: DriverModel
): boolean => !!modelId && modelId !== DefaultModelId && model?.brandId === DefaultBrandId;

/** A generic driver still awaiting a model; an unlisted-brand choice is a finished setup. */
export const needsDriverChoice = (modelId: T_Maybe<Model['id']>, model?: DriverModel): boolean =>
  isGenericDriver(modelId, model) && !isUnlistedBrandDriver(modelId, model);

/** Shared setup verdict for sidebar badges and selected-camera attention. */
export const deriveSetupAttention = (
  device: Gateable & T_Maybe<{ modelId?: T_Maybe<Model['id']>; agentStatus?: T_Maybe<string> }>,
  model?: DriverModel,
  status?: T_Maybe<{
    video?: T_Maybe<{ authenticationStatus?: T_Maybe<AuthenticationStatus> }>;
    movement?: T_Maybe<{ authenticationStatus?: T_Maybe<AuthenticationStatus> }>;
  }>
): SetupAttention | undefined => {
  if (!device) return undefined;
  // A placeholder has no authoritative room record or proxy until authentication succeeds.
  if (isTransitory(device)) return 'authentication';
  if (device.agentStatus === 'offline' || device.agentStatus === 'offline-embedded')
    return undefined;
  // Prefer reactive mapped fields; use raw status only before that aspect has been mapped.
  if (
    isAuthInvalid({
      videoAuthenticationStatus:
        device.videoAuthenticationStatus ?? status?.video?.authenticationStatus,
      controlAuthenticationStatus:
        device.controlAuthenticationStatus ?? status?.movement?.authenticationStatus
    })
  )
    return 'authentication';
  // An unknown template is not evidence of a generic model. The default ID is explicit evidence.
  if (needsDriverChoice(device.modelId, model)) return 'driver';
  return undefined;
};
